import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, CreditCard } from "lucide-react";
import { fmtDate, fmtMoney } from "@/lib/format";

const PLANS = [
  { id: "starter", name: "Starter", price: 29, bg: "#FFFFFF", features: ["1 bank account", "3 months history", "1 team member", "Single workspace"] },
  { id: "pro", name: "Pro", price: 79, featured: true, bg: "#F8FAFC", features: ["5 bank accounts per client workspace (add more at $3/mo)", "60 months history", "5 team members (add more at $10/mo up to 24)", "Up to 25 client workspaces", "Firm Clients module"] },
  { id: "business", name: "Business", price: 199, bg: "#FFFFFF", features: ["Unlimited bank accounts per client workspace", "60 months history", "25 team members", "Unlimited client workspaces", "Dedicated support"] },
];

export default function Billing() {
  const { tenant, user, refreshMe } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pollingMsg, setPollingMsg] = useState("");
  const [recurring, setRecurring] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const loadHistory = async () => {
    try { const r = await api.get("/billing/transactions"); setHistory(r.data); } catch {}
  };
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    api.get("/billing/plans").then((r) => setRecurring(r.data?.recurring)).catch(() => {});
  }, []);

  const checkout = async (planId) => {
    setLoading(true);
    try {
      const res = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  // ---- Pro-rate / downgrade flow ---------------------------------------------
  const [downgradeTarget, setDowngradeTarget] = useState(null);
  const [downgradePreview, setDowngradePreview] = useState(null);
  const [downgrading, setDowngrading] = useState(false);

  const handlePlanClick = async (p) => {
    const currentPrice = PLANS.find((x) => x.id === currentPlan)?.price ?? 0;
    if (p.price < currentPrice) {
      // Downgrade — show preview modal first.
      try {
        const { data } = await api.get(`/billing/downgrade-preview?target_plan=${p.id}`);
        setDowngradeTarget(p);
        setDowngradePreview(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load downgrade preview");
      }
    } else {
      // Upgrade or same price — go through Stripe.
      checkout(p.id);
    }
  };

  const confirmDowngrade = async () => {
    if (!downgradeTarget || !downgradePreview) return;
    setDowngrading(true);
    try {
      await api.post("/billing/plans/change", {
        target_plan: downgradeTarget.id,
        confirm: true,
      });
      toast.success(`Switched to ${downgradeTarget.name} plan`);
      setDowngradeTarget(null);
      setDowngradePreview(null);
      await refreshMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Downgrade failed");
    } finally {
      setDowngrading(false);
    }
  };

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 8) {
      setPollingMsg("Payment status check timed out. Please refresh.");
      return;
    }
    try {
      const r = await api.get(`/billing/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        setPollingMsg("Payment complete. Plan upgraded.");
        toast.success("Plan upgraded");
        await refreshMe();
        await loadHistory();
        return;
      }
      if (r.data.status === "expired") {
        setPollingMsg("Payment session expired.");
        return;
      }
      setPollingMsg("Verifying payment…");
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2500);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2500);
    }
  }, [refreshMe]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const sid = p.get("session_id");
    if (sid) {
      setPollingMsg("Verifying payment…");
      pollStatus(sid).finally(() => {
        // Clean URL
        navigate("/app/billing", { replace: true });
      });
    } else if (p.get("canceled")) {
      toast.info("Checkout canceled");
      navigate("/app/billing", { replace: true });
    }
  }, [location.search, pollStatus, navigate]);

  const currentPlan = tenant?.plan || "trial";

  return (
    <>
      <PageHeader title="Billing" subtitle="Subscription & payments" testid="billing-header" />
      <PageBody>
        {pollingMsg && (
          <div className="ll-card-flat p-4 mb-6 flex items-center gap-3" data-testid="polling-message">
            <div className="ll-progress flex-1" /> <span className="text-sm">{pollingMsg}</span>
          </div>
        )}

        {/* Recurring subscription setup status */}
        {recurring && user?.role === "owner" && (
          <div
            className="ll-card-flat p-4 mb-6 flex items-start gap-3"
            style={{
              background: recurring.enabled ? "#F0FDF4" : "#FFFBEB",
              borderColor: recurring.enabled ? "#86EFAC" : "#F59E0B",
            }}
            data-testid="recurring-status"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">
                {recurring.enabled
                  ? "Recurring Stripe subscriptions · active"
                  : "Recurring subscriptions · setup required"}
              </div>
              <p className="text-xs text-[var(--ll-ink-soft)] mt-1">
                {recurring.enabled
                  ? "Plans auto-renew every month via Stripe Billing. No action needed."
                  : (
                    <>
                      Plans currently charge once via Stripe Checkout. To enable monthly auto-renewal, create recurring Price objects in your Stripe dashboard and paste the IDs into <code className="px-1 bg-white border">backend/.env</code>:
                      {" "}
                      <code className="px-1 bg-white border">STRIPE_PRICE_STARTER</code>,
                      {" "}
                      <code className="px-1 bg-white border">STRIPE_PRICE_PRO</code>,
                      {" "}
                      <code className="px-1 bg-white border">STRIPE_PRICE_BUSINESS</code>
                      {" "}+ the two add-on price IDs. Restart the backend after saving.
                    </>
                  )}
              </p>
            </div>
          </div>
        )}
        <div className="ll-card-flat p-6 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="current-plan-box">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Current plan</div>
            <div className="font-display font-bold text-2xl mt-1 capitalize">{currentPlan}</div>
            <div className="text-sm text-[var(--ll-ink-soft)] mt-1">Status: {tenant?.subscription_status}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Limits</div>
            <div className="font-mono-fin text-sm mt-1">{tenant?.bank_account_limit} bank accounts · {tenant?.seats_limit} team members</div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px mb-8" style={{ background: "var(--ll-border)" }}>
          {PLANS.map((p) => {
            const isCurrent = currentPlan === p.id;
            return (
              <div
                key={p.id}
                className={`p-6 flex flex-col ${p.featured ? "ring-2 ring-[var(--ll-ink)]" : ""}`}
                style={{ background: p.bg || "var(--ll-bg-card)" }}
                data-testid={`plan-card-${p.id}`}
              >
                {p.featured && <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ochre)] mb-2">Most popular</div>}
                <div className="font-display font-bold text-2xl">{p.name}</div>
                <div className="font-display font-bold text-5xl mt-3 flex items-baseline gap-1">
                  <span className="text-xl text-[var(--ll-ink-soft)]">$</span>{p.price}<span className="text-base font-normal text-[var(--ll-ink-soft)]">/mo</span>
                </div>
                <ul className="mt-5 space-y-1.5 text-sm flex-1">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-2"><Check size={14} className="mt-0.5" />{f}</li>)}
                </ul>
                <button
                  onClick={() => handlePlanClick(p)}
                  disabled={loading || isCurrent || user?.role !== "owner"}
                  data-testid={`subscribe-${p.id}`}
                  className={`mt-6 px-5 py-3 transition-colors ${isCurrent ? "border bg-[var(--ll-mint)] cursor-default" : p.featured ? "bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f]" : "border border-[var(--ll-ink)] hover:bg-[var(--ll-mint)]"}`}
                  style={isCurrent ? { borderColor: "var(--ll-border)" } : undefined}
                >
                  {(() => {
                    if (isCurrent) return "Current plan";
                    if (user?.role !== "owner") return "Owner only";
                    if (loading) return "Redirecting…";
                    const currentPrice = PLANS.find((x) => x.id === currentPlan)?.price ?? 0;
                    if (p.price < currentPrice) return `Downgrade to ${p.name}`;
                    return `Subscribe to ${p.name}`;
                  })()}
                </button>
              </div>
            );
          })}
        </div>

        <div className="ll-card-flat overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
            <div className="font-display font-bold text-lg">Payment history</div>
            <CreditCard size={16} className="opacity-40" />
          </div>
          {history.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="payment-history-empty">No payments yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="payment-history-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium text-right">Amount</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
                    <td className="px-5 py-2.5 text-[var(--ll-ink-soft)]">{fmtDate(h.created_at)}</td>
                    <td className="px-5 py-2.5">{h.plan_id}</td>
                    <td className="px-5 py-2.5 text-right font-mono-fin">{fmtMoney(h.amount)}</td>
                    <td className="px-5 py-2.5 text-xs uppercase tracking-wider">{h.payment_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </PageBody>

      {/* Downgrade confirmation modal */}
      {downgradeTarget && downgradePreview && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !downgrading && setDowngradeTarget(null)}
          data-testid="downgrade-modal"
        >
          <div
            className="bg-white max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-display font-bold text-xl mb-2">
              Downgrade to {downgradeTarget.name}?
            </div>
            <p className="text-sm text-[var(--ll-ink-soft)] mb-4">
              You&apos;ll pay <strong>${downgradePreview.target_price}/mo</strong> going forward. Here&apos;s what changes:
            </p>
            {downgradePreview.excesses.length === 0 ? (
              <div className="text-sm bg-[#F0FDF4] border border-[#86EFAC] px-3 py-2 mb-4" data-testid="downgrade-safe">
                All your current usage fits comfortably within the new {downgradeTarget.name} caps.
              </div>
            ) : (
              <ul className="space-y-2 mb-4" data-testid="downgrade-excesses">
                {downgradePreview.excesses.map((e) => (
                  <li
                    key={e.resource}
                    className="text-sm border-l-2 pl-3 py-1"
                    style={{ borderColor: e.overflow > 0 ? "#DC2626" : "#F59E0B" }}
                    data-testid={`downgrade-excess-${e.resource}`}
                  >
                    {e.action}
                    {e.overflow > 0 && (
                      <div className="text-[11px] uppercase tracking-widest text-[#991B1B] mt-1">
                        {e.overflow} over the new cap — must be resolved first
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {downgradePreview.excesses.some((e) => e.overflow > 0) && (
              <div className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#F87171] px-3 py-2 mb-4" data-testid="downgrade-blocked-notice">
                Resolve the items marked &ldquo;over the new cap&rdquo; from the relevant pages before downgrading. We&apos;ll block the switch until they fit.
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDowngradeTarget(null)}
                disabled={downgrading}
                data-testid="downgrade-cancel"
                className="text-sm px-4 py-2 border hover:bg-[var(--ll-bg-card)]"
                style={{ borderColor: "var(--ll-border)" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDowngrade}
                disabled={downgrading || downgradePreview.excesses.some((e) => e.overflow > 0)}
                data-testid="downgrade-confirm"
                className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] disabled:opacity-60"
              >
                {downgrading ? "Switching…" : `Confirm downgrade`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
